<template>
  <Layout>
    <template #header-container>
      <div
        class="container-alert"
        v-if="
          showInfo &&
          !hasAccess([
            'service_order:read',
            'service_order:update',
            'service_order:create',
            'service_order:delete',
          ])
        "
      >
        <McAlert class="custom-alert" variant="info" withIcon>
          <template #label>
            <div
              class="custom-alert__text"
              v-html="$t('maintenance.tooltip.maintenanceOrder')"
            ></div>
          </template>
          <template #suffix>
            <div class="custom-alert__suffix">
              <a
                href="https://kb.mceasy.com/docs/perintah-servis/"
                target="_blank"
                class="text-decoration-underline"
              >
                {{ $t('maintenance.tooltip.learnMore') }}
              </a>
              <a class="text-muted" @click="handleInfo">
                <i class="mc-icon icon-outline_close"></i>
              </a>
            </div>
          </template>
        </McAlert>
      </div>
    </template>
    <div class="mc-list-header">
      <PageHeader :title="title" />
      <p class="mb-4">{{ $t('maintenance.menu.desc.alert') }}</p>

      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end d-flex flex-row">
          <McButton
            variant="secondary"
            @click="$router.push({ name: 'Email Service Alert' })"
            icon="icon-outline_email"
            :text="$t('global.reminderDelivery')"
          />
          <b-spinner
            class="mx-4"
            v-if="isExporting"
            variant="primary"
            label="Loading..."
          ></b-spinner>
          <McButton
            v-else
            variant="secondary"
            class="mx-2"
            @click="printScheduler"
            :text="$t('global.print')"
            icon="icon-outline_printer"
          />
          <McButton
            @click="$router.push({ name: 'Service' })"
            variant="secondary"
            :text="$t('global.back')"
            icon="icon-outline_arrow-left"
          />
        </div>
      </div>
    </div>
    <b-skeleton-wrapper :loading="loading">
      <template #loading>
        <b-row>
          <b-col md="3">
            <b-card class="mc-dashboard-card no-hover">
              <div class="card-body d-flex justify-content-between">
                <b-skeleton width="70%" height="40px"></b-skeleton>
                <div class="avatar-sm">
                  <div class="avatar-title rounded-circle bg-danger font-size-20">
                    <i class="icon-outline_alert-triangle mc-fa"></i>
                  </div>
                </div>
              </div>
            </b-card>
          </b-col>
          <b-col md="3">
            <b-card class="mc-dashboard-card no-hover">
              <div class="card-body d-flex justify-content-between">
                <b-skeleton width="70%" height="40px"></b-skeleton>
                <div class="avatar-sm">
                  <div class="avatar-title rounded-circle bg-warning font-size-20">
                    <i class="icon-outline_timer mc-fa"></i>
                  </div>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </template>
      <b-row class="mc-dashboard-card no-hover">
        <b-col class="mc-dashboard-card__item" md="3">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="row">
                <div class="col-md-9">
                  <p class="text-muted">{{ $t('maintenance.status.lateService') }}</p>
                  <h5 class="font-size-13 mb-0">{{ data.totalLate }}</h5>
                </div>
                <div class="avatar-sm">
                  <div class="avatar-title rounded-circle bg-danger font-size-20">
                    <i class="icon-outline_alert-triangle mc-fa"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-col>
        <b-col class="mc-dashboard-card__item" md="3">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="row">
                <div class="col-md-9">
                  <p class="text-muted">{{ $t('maintenance.status.approachingService') }}</p>
                  <h5 class="font-size-13 mb-0">{{ data.totalSoon }}</h5>
                </div>
                <div class="avatar-sm">
                  <div class="avatar-title rounded-circle bg-warning font-size-20">
                    <i class="icon-outline_timer mc-fa"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-skeleton-wrapper>

    <div class="row">
      <div class="col-12">
        <!-- Table -->
        <div class="table-responsive my-3" v-if="!isGrouping">
          <McGrapqlTable
            v-show="selectedTab === 'DistanceAndTime' && render"
            :apiClient="query"
            :fields="localizeDistanceFields"
            :customHandlers="customHandlers"
            :searchable="true"
            :withCard="true"
            :stickyHeader="true"
            :refreshData="!loading"
            trackBy="id"
            :hasDelete="false"
            :filterBottom="true"
            @handleSelectValue="handleSelected"
          >
            <template #button-filter>
              <div class="d-flex justify-content-end">
                <b-dropdown
                  id="action-dropdown"
                  right
                  variant="primary"
                  menu-class="dropdown-menu-end"
                  :disabled="selectedItems.length == 0"
                >
                  <template v-slot:button-content>
                    {{ $t('global.field.action') }} <i class="mc-fa icon-outline_chevron-down"></i>
                  </template>
                  <b-dropdown-item href="#">
                    <b-button class="mc-button-action d-flex" v-b-modal.mc-modal-change-to-service>
                      {{ $t('maintenance.changeTo') }}
                      <i class="mc-fa icon-outline_arrow-right font-size-20"></i>
                      <span class="status">
                        {{ $t('maintenance.status.inService') }}
                      </span>
                    </b-button>
                  </b-dropdown-item>
                  <b-dropdown-item href="#">
                    <div class="d-flex flex-column text-center">
                      <b-button
                        class="mc-button-action"
                        :disabled="
                          !hasAccess([
                            'service_order:read',
                            'service_order:update',
                            'service_order:create',
                            'service_order:delete',
                          ])
                        "
                        v-b-modal.mc-modal-create-mo
                      >
                        {{ $t('maintenance.createMO') }}
                      </b-button>
                      <span
                        class="text-muted"
                        v-if="
                          !hasAccess([
                            'service_order:read',
                            'service_order:update',
                            'service_order:create',
                            'service_order:delete',
                          ])
                        "
                      >
                        {{ $t('maintenance.MOUnavailable') }}
                      </span>
                    </div>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </template>

            <template #tab-button>
              <div class="tab-button-service-schedule mc-btn--tab mb-3">
                <b-form-radio-group
                  id="tab-service-schedule"
                  v-model="selectedTab"
                  :options="tabOptions"
                  name="tab-service-schedule"
                  buttons
                  @change="refreshTable"
                ></b-form-radio-group>
              </div>
            </template>

            <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
              <McFilterSelect
                :defaultColumnOrder="initialFields"
                :currentColumnOrder="dataFields"
                @setColumn="handleSetDataFields"
                @setField="handleFieldShow"
                @setGroupView="handleGroupView"
                hasGrouping
                :groupTableValue="isGrouping"
              />
            </template>

            <template #head(startServiceAtDistance)="data">
              {{ `${data.label} (Km)` }}
            </template>

            <template #head(serviceDistanceDifference)="data">
              {{ `${data.label} (Km)` }}
            </template>

            <template #cell(status)="data">
              <span :class="`mc-status mc-status--${data.value.color}`">
                {{ data.value.label }}
              </span>
            </template>

            <template #cell(vehicle.odometer)="data">
              {{ thousandFormat(data.value) }}
            </template>

            <template #cell(startServiceAtDistance)="data">
              <span v-if="data.item.status.includes('LO')" class="text-danger">
                {{ thousandFormat(data.value) }}
              </span>
              <span v-else-if="data.item.status.includes('SO')" class="text-warning">
                {{ thousandFormat(data.value) }}
              </span>
              <span v-else class="text-dark"> {{ thousandFormat(data.value) }} </span>
            </template>

            <template #cell(startServiceAtHourmeter)="data">
              <span v-if="data.item.status.includes('LH')" class="text-danger">
                {{ minuteToHour(data.value) }}
              </span>
              <span v-else-if="data.item.status.includes('SH')" class="text-warning">
                {{ minuteToHour(data.value) }}
              </span>
              <span v-else class="text-dark"> {{ minuteToHour(data.value) }} </span>
            </template>

            <template #cell(startServiceAtTime)="data">
              <span v-if="data.item.status.includes('LD')" class="text-danger">
                {{ momentDateShortMonth(data.value) }}
              </span>
              <span v-else-if="data.item.status.includes('SD')" class="text-warning">
                {{ momentDateShortMonth(data.value) }}
              </span>
              <span v-else class="text-dark">
                {{ momentDateShortMonth(data.value) }}
              </span>
            </template>

            <template #cell(vehicle.hourmeter)="data">
              {{ minuteToHour(data.value) }}
            </template>

            <template #cell(serviceDistanceDifference)="data">
              {{ thousandFormat(data.value) }}
            </template>

            <template #cell(serviceHourmeterDifference)="data">
              {{ minuteToHour(data.value) }}
            </template>

            <template #cell(serviceTimeDifference.readableText)="data">
              {{ data.item.serviceTimeDifference.isDatePassed ? '-' : ''
              }}{{
                data.item.serviceTimeDifference.months == 0 &&
                data.item.serviceTimeDifference.days == 0
                  ? `0 ${$t('global.time.days')}`
                  : `${
                      data.item.serviceTimeDifference.months
                        ? data.item.serviceTimeDifference.months + ` ${$t('global.time.month')} `
                        : ''
                    }${
                      data.item.serviceTimeDifference.days
                        ? data.item.serviceTimeDifference.days + ` ${$t('global.time.days')}`
                        : ''
                    }`
              }}
            </template>

            <template #cell(workshop.latLong)="data">
              <a
                class="link-primary"
                :href="`https://www.google.com/maps/search/?api=1&query=${data.value.lat},${data.value.long}`"
                target="_blank"
                v-if="data.value.lat && data.value.long"
                ><i class="fas fa-link"></i
              ></a>
              <span v-else>-</span>
            </template>
          </McGrapqlTable>

          <McGrapqlTable
            v-show="selectedTab === 'Hourmeter' && render"
            :apiClient="query"
            :fields="localizeHourmeterFields"
            :customHandlers="customHandlers"
            :searchable="true"
            :withCard="true"
            :stickyHeader="true"
            :refreshData="!loading"
            trackBy="id"
            :hasDelete="false"
            :filterBottom="true"
            @handleSelectValue="handleSelected"
          >
            <template #button-filter>
              <div class="d-flex justify-content-end">
                <b-dropdown
                  id="action-dropdown"
                  right
                  variant="primary"
                  menu-class="dropdown-menu-end"
                  :disabled="selectedItems.length == 0"
                >
                  <template v-slot:button-content>
                    {{ $t('global.field.action') }} <i class="mc-fa icon-outline_chevron-down"></i>
                  </template>
                  <b-dropdown-item href="#">
                    <b-button class="mc-button-action d-flex" v-b-modal.mc-modal-change-to-service>
                      {{ $t('maintenance.changeTo') }}
                      <i class="mc-fa icon-outline_arrow-right font-size-20"></i>
                      <span class="status">
                        {{ $t('maintenance.status.inService') }}
                      </span>
                    </b-button>
                  </b-dropdown-item>
                  <b-dropdown-item href="#">
                    <div class="d-flex flex-column text-center">
                      <b-button
                        class="mc-button-action"
                        :disabled="
                          !hasAccess([
                            'service_order:read',
                            'service_order:update',
                            'service_order:create',
                            'service_order:delete',
                          ])
                        "
                        v-b-modal.mc-modal-create-mo
                      >
                        {{ $t('maintenance.createMO') }}
                      </b-button>
                      <span
                        class="text-muted"
                        v-if="
                          !hasAccess([
                            'service_order:read',
                            'service_order:update',
                            'service_order:create',
                            'service_order:delete',
                          ])
                        "
                      >
                        {{ $t('maintenance.MOUnavailable') }}
                      </span>
                    </div>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </template>

            <template #tab-button>
              <div class="tab-button-service-schedule mc-btn--tab mb-3">
                <b-form-radio-group
                  id="tab-service-schedule"
                  v-model="selectedTab"
                  :options="tabOptions"
                  name="tab-service-schedule"
                  buttons
                  @change="refreshTable"
                ></b-form-radio-group>
              </div>
            </template>

            <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
              <McFilterSelect
                :defaultColumnOrder="initialFields"
                :currentColumnOrder="dataFields"
                @setColumn="handleSetDataFields"
                @setField="handleFieldShow"
                @setGroupView="handleGroupView"
                hasGrouping
                :groupTableValue="isGrouping"
              />
            </template>

            <template #cell(status)="data">
              <span :class="`mc-status mc-status--${data.value.color}`">
                {{ data.value.label }}
              </span>
            </template>

            <template #cell(vehicle.odometer)="data">
              {{ thousandFormat(data.value) }}
            </template>

            <template #cell(startServiceAtDistance)="data">
              <span v-if="data.item.status.includes('LO')" class="text-danger">
                {{ thousandFormat(data.value) }}
              </span>
              <span v-else-if="data.item.status.includes('SO')" class="text-warning">
                {{ thousandFormat(data.value) }}
              </span>
              <span v-else class="text-dark"> {{ thousandFormat(data.value) }} </span>
            </template>

            <template #cell(startServiceAtHourmeter)="data">
              <span v-if="data.item.status.includes('LH')" class="text-danger">
                {{ minuteToHour(data.value) }}
              </span>
              <span v-else-if="data.item.status.includes('SH')" class="text-warning">
                {{ minuteToHour(data.value) }}
              </span>
              <span v-else class="text-dark"> {{ minuteToHour(data.value) }} </span>
            </template>

            <template #cell(startServiceAtTime)="data">
              <span v-if="data.item.status.includes('LD')" class="text-danger">
                {{ momentDateShortMonth(data.value) }}
              </span>
              <span v-else-if="data.item.status.includes('SD')" class="text-warning">
                {{ momentDateShortMonth(data.value) }}
              </span>
              <span v-else class="text-dark">
                {{ momentDateShortMonth(data.value) }}
              </span>
            </template>

            <template #cell(vehicle.hourmeter)="data">
              {{ minuteToHour(data.value) }}
            </template>

            <template #cell(serviceDistanceDifference)="data">
              {{ thousandFormat(data.value) }}
            </template>

            <template #cell(serviceHourmeterDifference)="data">
              {{ minuteToHour(data.value) }}
            </template>

            <template #cell(serviceTimeDifference.readableText)="data">
              {{ data.item.serviceTimeDifference.isDatePassed ? '-' : ''
              }}{{
                data.item.serviceTimeDifference.months == 0 &&
                data.item.serviceTimeDifference.days == 0
                  ? `0 ${$t('global.time.days')}`
                  : `${
                      data.item.serviceTimeDifference.months
                        ? data.item.serviceTimeDifference.months + ` ${$t('global.time.month')} `
                        : ''
                    }${
                      data.item.serviceTimeDifference.days
                        ? data.item.serviceTimeDifference.days + ` ${$t('global.time.days')}`
                        : ''
                    }`
              }}
            </template>

            <template #cell(workshop.latLong)="data">
              <a
                class="link-primary"
                :href="`https://www.google.com/maps/search/?api=1&query=${data.value.lat},${data.value.long}`"
                target="_blank"
                v-if="data.value.lat && data.value.long"
                ><i class="fas fa-link"></i
              ></a>
              <span v-else>-</span>
            </template>
          </McGrapqlTable>
        </div>
        <div class="table-responsive my-3" v-else>
          <GroupByTable>
            <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
              <McFilterSelect
                :defaultColumnOrder="initialFields"
                :currentColumnOrder="dataFields"
                @setColumn="handleSetDataFields"
                @setGroupView="handleGroupView"
                hasGrouping
                :groupTableValue="isGrouping"
              />
            </template>
          </GroupByTable>
        </div>
      </div>
    </div>
    <b-modal
      class="mc-modal"
      id="mc-modal-change-to-service"
      ref="mc-modal-change-to-service"
      :title="$t('maintenance.changeToService')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p class="mc-general__label">
          {{ $t('global.text.modal.changeToService', { total: selectedItems.length }) }}
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            :text="$t('global.action.cancel')"
            class="me-2"
            @click="cancel()"
          />
          <McButton
            variant="primary"
            :text="$t('maintenance.changeToService')"
            @click="changeToService"
          />
        </div>
      </template>
    </b-modal>
    <b-modal
      class="mc-modal"
      id="mc-modal-create-mo"
      ref="mc-modal-create-mo"
      :title="$t('maintenance.createMO')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p class="mc-general__label">
          {{ $t('maintenance.modal.createMo', { total: selectedItems.length }) }}
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            :text="$t('global.action.cancel')"
            class="me-2"
            @click="cancel()"
          />
          <McButton
            variant="primary"
            :text="$t('global.action.continue')"
            @click="handleCreateMo"
          />
        </div>
      </template>
    </b-modal>
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import McGrapqlTable from '@/components/McGrapqlTable'
import McButton from '@/components/McButton'
import McFilterSelect from '@/components/McFilterSelect'
import McAlert from '@/components/McAlert'
import GroupByTable from './components/GroupByTable.vue'
import {
  GET_SERVICE_SCHEDULES,
  UPDATE_SERVICE_SCHEDULE,
} from '@/services/api/maintenance/serviceSchedules'
import { Helper } from '@/mixin/helper'
import { EXPORT_SERVICE_SCHEDULE } from '@/services/api/report/report'
import { MixPanel } from '@/mixin/mixpanel'
import { mapGetters } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'

/**
 * Starter component
 */
export default {
  page: {
    title: 'Servis',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
    McGrapqlTable,
    McFilterSelect,
    McButton,
    McAlert,
    GroupByTable,
  },

  mixins: [Helper, MixPanel],

  apollo: {
    serviceSchedules: {
      fetchPolicy: 'no-cache',
      query: GET_SERVICE_SCHEDULES,
      variables() {
        return {
          filter: {
            status: [
              'lateOdometer',
              'lateDate',
              'soonOdometer',
              'soonDate',
              'soonHourmeter',
              'lateHourmeter',
            ],
            vehicleStatus: ['I', 'R'],
            type: ['Hourmeter', 'DistanceAndTime'],
            pagination: {
              page: 1,
              show: 99999,
            },
          },
        }
      },
      result({ data, loading }) {
        if (loading) return
        this.data = data.serviceSchedules.serviceSchedules.data
        const hourmeterData = data.serviceSchedules.serviceSchedules.data.serviceSchedules.filter(
          (item) => item.type === 'Hourmeter'
        )
        this.hourmeterData = hourmeterData.length

        const kilometerData = data.serviceSchedules.serviceSchedules.data.serviceSchedules.filter(
          (item) => item.type === 'DistanceAndTime'
        )
        this.kilometerData = kilometerData.length

        if (this.kilometerData == 0) this.selectedTab = 'Hourmeter'
        else this.selectedTab = 'DistanceAndTime'
        this.loading = false
      },
    },
  },

  data() {
    return {
      customHandlers: {
        handleClickRow: (item) => {
          return this.$router.push({
            name: 'Detail Service Schedule',
            params: { vehicleId: item.vehicleId },
          })
        },
      },
      data: {},
      loading: true,
      created: true,
      isExporting: false,
      hourmeterData: 0,
      kilometerData: 0,
      selectedTab: 'DistanceAndTime',
      distanceFieldShow: JSON.parse(localStorage.getItem('saDistanceFields')),
      hourmeterFieldShow: JSON.parse(localStorage.getItem('saHourmeterFields')),
      render: true,
      showInfo: true,
      isGrouping: true,
      selectedItems: [],
    }
  },

  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    title() {
      return this.$t('maintenance.frontPage.title.serviceAlert')
    },

    optionalFields() {
      let fields = []

      if (
        this.hasAccess([
          'maintenance-group:read',
          'maintenance-group:update',
          'maintenance-group:create',
          'maintenance-group:delete',
        ])
      ) {
        fields = [
          {
            key: 'ssgroup.name',
            label: 'maintenance.frontPage.title.serviceGroup',
            isSortable: true,
            formatter: (val) => {
              return val ?? '-'
            },
          },
        ]
      }
      return fields
    },

    tabOptions() {
      let tab = []
      if (this.hourmeterData > 0 && this.kilometerData > 0) {
        tab = [
          {
            text: `${this.$t('maintenance.hourmeter.kilometerTime')} ( ${this.kilometerData} )`,
            value: 'DistanceAndTime',
          },
          {
            text: `${this.$t('maintenance.hourmeter.workingHours')} ( ${this.hourmeterData} )`,
            value: 'Hourmeter',
          },
        ]
      }
      return tab
    },

    distanceStoredFields() {
      for (const field of this.distanceFieldShow.column) {
        if (field.key == 'status') {
          field.formatter = (val) => {
            let format = { color: 'green', label: this.$t('maintenance.status.normal') }
            if (val.includes('I')) {
              format = { color: 'blue', label: this.$t('maintenance.status.inService') }
            } else if (val.includes('LO') || val.includes('LD' || val.includes('LH'))) {
              format = { color: 'red', label: this.$t('maintenance.status.lateService') }
            } else if (val.includes('SO') || val.includes('SD') || val.includes('SH')) {
              format = { color: 'yellow', label: this.$t('maintenance.status.approachingService') }
            }

            return format
          }
        }
        if (field.key == 'service.name') {
          field.formatter = (val, key, item) => {
            return `${val} (${item.service.serviceAction.action})`
          }
        }
        if (
          field.key == 'workshop.name' ||
          field.key == 'workshop.address' ||
          field.key == 'ssgroup.name'
        ) {
          field.formatter = (val) => {
            return val ?? '-'
          }
        }
      }
      return this.distanceFieldShow.column
    },

    hourmeterStoredFields() {
      for (const field of this.hourmeterFieldShow.column) {
        if (field.key == 'status') {
          field.formatter = (val) => {
            let format = { color: 'green', label: this.$t('maintenance.status.normal') }
            if (val.includes('I')) {
              format = { color: 'blue', label: this.$t('maintenance.status.inService') }
            } else if (val.includes('LO') || val.includes('LD') || val.includes('LH')) {
              format = { color: 'red', label: this.$t('maintenance.status.lateService') }
            } else if (val.includes('SO') || val.includes('SD') || val.includes('SH')) {
              format = { color: 'yellow', label: this.$t('maintenance.status.approachingService') }
            }

            return format
          }
        }
        if (field.key == 'service.name') {
          field.formatter = (val, key, item) => {
            return `${val} (${item.service.serviceAction.action})`
          }
        }
        if (
          field.key == 'workshop.name' ||
          field.key == 'workshop.address' ||
          field.key == 'ssgroup.name'
        ) {
          field.formatter = (val) => {
            return val ?? '-'
          }
        }
        if (field.key == 'vehicle.hourmeter') {
          field.formatter = (val) => {
            return val ?? 0
          }
        }
      }
      return this.hourmeterFieldShow.column
    },

    distanceFields() {
      if (this.distanceFieldShow) {
        return this.distanceStoredFields
      } else {
        return [
          {
            key: 'vehicle.licensePlate',
            label: 'global.object.licensePlate',
            isSortable: true,
            isRedirectable: true,
            tdAttr: {
              style:
                'position: -webkit-sticky;position: sticky;background-color: white;width: 160px;min-width: 160px;max-width: 160px;left: 0px;z-index:1',
            },
            thAttr: {
              style:
                'position: -webkit-sticky;position: sticky;background-color: white;width: 160px;min-width: 160px;max-width: 160px;left: 0px;z-index:1',
            },
            isLocked: true,
            isSticky: true,
          },
          {
            key: 'status',
            label: 'maintenance.field.statusService',
            isSortable: true,
            tdAttr: {
              style:
                'position: -webkit-sticky;position: sticky;background-color: white;width: 180px;min-width: 180px;max-width: 180px;left: 160px;z-index:1',
            },
            thAttr: {
              style:
                'position: -webkit-sticky;position: sticky;background-color: white;width: 180px;min-width: 180px;max-width: 180px;left: 160px;z-index:1',
            },
            formatter: (val) => {
              let format = { color: 'green', label: this.$t('maintenance.status.normal') }
              if (val.includes('I')) {
                format = { color: 'blue', label: this.$t('maintenance.status.inService') }
              } else if (val.includes('LO') || val.includes('LD') || val.includes('LH')) {
                format = { color: 'red', label: this.$t('maintenance.status.lateService') }
              } else if (val.includes('SO') || val.includes('SD') || val.includes('SH')) {
                format = {
                  color: 'yellow',
                  label: this.$t('maintenance.status.approachingService'),
                }
              }

              return format
            },
            isLocked: true,
            isSticky: true,
          },
          {
            key: 'service.name',
            label: 'maintenance.frontPage.title.serviceSchedule',
            isSortable: true,
            formatter: (val, key, item) => {
              return `${val} (${item.service.serviceAction.action})`
            },
          },
          ...this.optionalFields,
          {
            key: 'method',
            label: 'maintenance.methodScheduler',
            shown: false,
          },
          {
            key: 'vehicle.odometer',
            label: 'maintenance.form.currentKilometer',
          },
          {
            key: 'startServiceAtDistance',
            label: 'maintenance.form.odoNextService',
          },
          {
            key: 'serviceDistanceDifference',
            label: 'maintenance.form.kilometerDifference',
            isSortable: true,
          },
          {
            key: 'startServiceAtTime',
            label: 'maintenance.form.dateNextService',
            isSortable: true,
          },
          {
            key: 'serviceTimeDifference.readableText',
            label: 'maintenance.form.timeDifference',
            isSortable: true,
          },
          {
            key: 'workshop.name',
            label: 'maintenance.form.workshopName',
            isSortable: true,
            formatter: (val) => {
              return val ?? '-'
            },
          },
          {
            key: 'workshop.address',
            label: 'maintenance.form.workshopAddress',
            isSortable: true,
            formatter: (val) => {
              return val ?? '-'
            },
          },
          // Kolom di-hide karena value akan selalu kosong
          // Menunggu kepastian kapan data akan terisi
          // {
          //   key: 'workingDuration',
          //   label: this.$t('maintenance.form.workingDuration'),
          //   isSortable: true,
          //   formatter: (val) => {
          //     return val??'-'
          //   },
          // },
          // {
          //   key: 'workingHour',
          //   label: this.$t('maintenance.form.workingHour'),
          //   isSortable: true,
          //   formatter: (val) => {
          //     return val?this.momentDMHm(val):'-'
          //   },
          // },
          {
            key: 'workshop.latLong',
            label: 'maintenance.form.workshopLocationLink',
            isSortable: false,
          },
        ]
      }
    },
    localizeDistanceFields() {
      const fields = cloneDeep(this.distanceFields)
      fields.forEach((item) => {
        item.label = `${this.$t(item.label)}`
      })
      return fields
    },
    hourmeterFields() {
      if (this.hourmeterFieldShow) {
        return this.hourmeterStoredFields
      } else {
        return [
          {
            key: 'vehicle.licensePlate',
            label: 'global.object.licensePlate',
            isSortable: true,
            isRedirectable: true,
            tdAttr: {
              style:
                'position: -webkit-sticky;position: sticky;background-color: white;width: 160px;min-width: 160px;max-width: 160px;left: 0px;z-index:1',
            },
            thAttr: {
              style:
                'position: -webkit-sticky;position: sticky;background-color: white;width: 160px;min-width: 160px;max-width: 160px;left: 0px;z-index:1',
            },
            isLocked: true,
            isSticky: true,
          },
          {
            key: 'status',
            label: 'maintenance.field.statusService',
            isSortable: true,
            tdAttr: {
              style:
                'position: -webkit-sticky;position: sticky;background-color: white;width: 180px;min-width: 180px;max-width: 180px;left: 160px;z-index:1',
            },
            thAttr: {
              style:
                'position: -webkit-sticky;position: sticky;background-color: white;width: 180px;min-width: 180px;max-width: 180px;left: 160px;z-index:1',
            },
            formatter: (val) => {
              let format = { color: 'green', label: this.$t('maintenance.status.normal') }
              if (val.includes('I')) {
                format = { color: 'blue', label: this.$t('maintenance.status.inService') }
              } else if (val.includes('LO') || val.includes('LD') || val.includes('LH')) {
                format = { color: 'red', label: this.$t('maintenance.status.lateService') }
              } else if (val.includes('SO') || val.includes('SD') || val.includes('SH')) {
                format = {
                  color: 'yellow',
                  label: this.$t('maintenance.status.approachingService'),
                }
              }

              return format
            },
            isLocked: true,
            isSticky: true,
          },
          {
            key: 'service.name',
            label: 'maintenance.frontPage.title.serviceSchedule',
            isSortable: true,
            formatter: (val, key, item) => {
              return `${val} (${item.service.serviceAction.action})`
            },
          },
          ...this.optionalFields,
          {
            key: 'method',
            label: 'maintenance.methodScheduler',
            shown: false,
          },
          {
            key: 'vehicle.hourmeter',
            label: 'maintenance.hourmeter.totalDuration',
            isSortable: true,
            formatter: (val) => {
              return val ?? 0
            },
          },
          {
            key: 'startServiceAtHourmeter',
            label: 'maintenance.hourmeter.nextWorkingHours',
            isSortable: true,
          },
          {
            key: 'serviceHourmeterDifference',
            label: 'maintenance.hourmeter.serviceHoursDifference',
            isSortable: true,
          },
          {
            key: 'workshop.name',
            label: 'maintenance.form.workshopName',
            isSortable: true,
            formatter: (val) => {
              return val ?? '-'
            },
          },
          {
            key: 'workshop.address',
            label: 'maintenance.form.workshopAddress',
            isSortable: true,
            formatter: (val) => {
              return val ?? '-'
            },
          },
          // Kolom di-hide karena value akan selalu kosong
          // Menunggu kepastian kapan data akan terisi
          // {
          //   key: 'workingDuration',
          //   label: this.$t('maintenance.form.workingDuration'),
          //   isSortable: true,
          //   formatter: (val) => {
          //     return val??'-'
          //   },
          // },
          // {
          //   key: 'workingHour',
          //   label: this.$t('maintenance.form.workingHour'),
          //   isSortable: true,
          //   formatter: (val) => {
          //     return val?this.momentDMHm(val):'-'
          //   },
          // },
          {
            key: 'workshop.latLong',
            label: 'maintenance.form.workshopLocationLink',
            isSortable: false,
          },
        ]
      }
    },
    localizeHourmeterFields() {
      const fields = cloneDeep(this.hourmeterFields)
      fields.forEach((item) => (item.label = `${this.$t(item.label)}`))
      return fields
    },
    query() {
      return {
        get: async (filter) => {
          return this.get(filter)
        },
      }
    },
  },

  watch: {
    '$i18n.locale': {
      deep: true,
      handler(e) {
        this.render = false

        setTimeout(() => {
          this.render = true
        }, 1000)
      },
    },
  },

  created() {
    this.setDefaultFields()
  },

  mounted() {
    this.track('View Service Reminder Page')
    this.showInfo = localStorage.getItem('subscriptionInfo')
      ? localStorage.getItem('subscriptionInfo')
      : true
  },

  methods: {
    setDefaultFields() {
      const defaultKilometer = this.distanceFields.map((field) => {
        if (!field.hasOwnProperty('shown')) field.shown = true
        return field
      })
      if (!localStorage.getItem('saDistanceFields')) {
        localStorage.setItem('saDistanceFields', JSON.stringify({ column: defaultKilometer }))
      }

      const defaultHourmeter = this.hourmeterFields.map((field) => {
        if (!field.hasOwnProperty('shown')) field.shown = true
        return field
      })
      if (!localStorage.getItem('saHourmeterFields')) {
        localStorage.setItem('saHourmeterFields', JSON.stringify({ column: defaultHourmeter }))
      }
    },

    async get(filter) {
      if (this.created) filter.orderBy = [JSON.stringify({ status: 'asc' })]
      this.created = false

      const res = await this.$apollo.query({
        fetchPolicy: 'no-cache',
        query: GET_SERVICE_SCHEDULES,
        variables: {
          filter: {
            ...filter,
            status: [
              'lateOdometer',
              'lateDate',
              'soonOdometer',
              'soonDate',
              'soonHourmeter',
              'lateHourmeter',
            ],
            vehicleStatus: ['I', 'R'],
            type: this.selectedTab,
          },
        },
      })

      res.data.serviceSchedules.serviceSchedules.data.serviceSchedules.forEach((row) => {
        row.id = `${row.vehicleId}-${row.serviceId}`
        if (row.ssgroup) {
          const ssgroupData = row.ssgroup?.service2Ssgroup?.find(
            (item) => item.serviceId == row.serviceId
          )
          if (ssgroupData) {
            row.intervalHourmeter = ssgroupData.intervalHourmeter
            row.reminderHourmeter = ssgroupData.reminderHourmeter
            row.intervalDistance = ssgroupData.intervalDistance
            row.reminderDistance = ssgroupData.reminderDistance
            row.intervalTime = ssgroupData.intervalTime
            row.intervalPeriod = ssgroupData.intervalPeriod
            row.reminderTime = ssgroupData.reminderTime
            row.reminderPeriod = ssgroupData.reminderPeriod
          }
        }
      })

      const serviceSchedules = res.data.serviceSchedules.serviceSchedules.data.serviceSchedules

      const result = {
        data: serviceSchedules,
        pagination: res.data.serviceSchedules.serviceSchedules.pagination,
      }

      this.loading = false

      return result
    },
    printScheduler() {
      this.track('Do Download/Print Service Reminder Data')
      this.isExporting = true

      const self = this
      this.$apollo
        .query({
          query: EXPORT_SERVICE_SCHEDULE,
          fetchPolicy: 'no-cache',
          variables: {
            filter: {
              column: [
                'licensePlate',
                'serviceStatus',
                'service',
                'serviceGroup',
                'serviceOdometer',
                'startServiceAtDistance',
                'serviceDistanceDifference',
                'startServiceAtTime',
                'serviceTimeDifference',
                'workshopName',
                'workshopAddress',
                // 'workingDuration',
                // 'workingHour',
                'workshopLinkLocation',
              ],
              hourmeterColumn: [
                'licensePlate',
                'serviceStatus',
                'service',
                'serviceGroup',
                'serviceHourmeter',
                'startServiceAtHourmeter',
                'serviceHourmeterDifference',
                'workshopName',
                'workshopAddress',
                // 'workingDuration',
                // 'workingHour',
                'workshopLinkLocation',
              ],
              downloadAs: 'excel',
              subtitle: `Tanggal ${this.momentDMYH(new Date())}`,
              doDefaultDateAssignment: false,
              status: [
                'lateOdometer',
                'lateDate',
                'soonOdometer',
                'soonDate',
                'lateHourmeter',
                'soonHourmeter',
              ],
              vehicleStatus: ['service', 'repair'],
            },
          },
        })
        .then((res) => {
          let isSuccessful = res.data.serviceSchedulesReport.isSuccessful
          if (isSuccessful) {
            var a = document.createElement('a') //Create <a>
            a.href =
              'data:application/vnd.ms-excel;base64,' +
              res.data.serviceSchedulesReport.serviceSchedulesReport.base64 //Image Base64 Goes here
            a.download = `Pengingat Servis ${self.momentYmDHms(new Date())}.xlsx` //File name Here
            a.click() //Downloaded file
          } else {
            self.$mcToast.error('Terdapat Kesalahan pada Server')
          }
          this.isExporting = false
        })
    },
    refreshTable() {
      this.loading = true
      const tab = this.selectedTab
      this.selectedTab = ''

      setTimeout(() => {
        this.selectedTab =
          this.hourmeterData === 0
            ? 'DistanceAndTime'
            : this.kilometerData === 0
            ? 'Hourmeter'
            : tab
        this.loading = false
      }, 500)
    },
    handleFieldShow(fields) {
      let savedAs = 'saDistanceFields'
      if (this.selectedTab == 'Hourmeter') {
        savedAs = 'saHourmeterFields'
      }

      localStorage.setItem(savedAs, JSON.stringify({ column: fields }))
    },
    handleInfo() {
      this.showInfo = false
      localStorage.setItem('subscriptionInfo', this.showInfo)
    },
    handleGroupView(value) {
      this.isGrouping = value
    },
    handleSelected(value) {
      const filteredIds = Object.entries(value)
        .filter(([key, value]) => value)
        .map(([key]) => {
          const [vehicleId, serviceId] = key.split('-')
          return {
            vehicleId: parseInt(vehicleId, 10),
            serviceId: parseInt(serviceId, 10),
          }
        })
      this.selectedItems = this.data.serviceSchedules.filter((item) =>
        filteredIds.some(
          (idItem) => item.vehicleId === idItem.vehicleId && item.serviceId === idItem.serviceId
        )
      )
    },
    async changeToService() {
      this.$bvModal.hide('mc-modal-change-to-service')

      let promises = []
      for (const item of this.selectedItems) {
        promises.push(
          this.$apollo.mutate({
            mutation: UPDATE_SERVICE_SCHEDULE,
            variables: {
              input: {
                newData: { forceInProgress: !item.forceInProgress },
                oldData: { forceInProgress: item.forceInProgress },
                vehicleId: item.vehicleId,
                serviceId: item.serviceId,
                type: item.type,
              },
            },
          })
        )
      }

      await Promise.all(promises).then((res) => {
        this.refreshTable()
        res.forEach((item) => {
          if (item.data.updateServiceSchedule.isSuccessful) {
            this.$mcToast.success(this.$t('global.alert.text.changed'))
          } else {
            this.$mcToast.error(item.data.updateServiceSchedule.message)
          }
          this.selectedItems = []
        })
      })
    },
    handleCreateMo() {
      const checkId = (array) => {
        if (array.length === 0) return true // If the array is empty, return true

        const firstVehicleId = array[0].vehicleId

        return array.every((item) => item.vehicleId === firstVehicleId)
      }
      const validation = checkId(this.selectedItems)

      if (validation) {
        const data = {
          vehicle: this.selectedItems[0].vehicle,
          services: this.selectedItems,
        }
        this.$store.commit('maintenance/SET_SHORTCUT_MO', data)
        this.$router.push({ name: 'Maintenance Create Service Order' })
      } else {
        this.$mcToast.error(this.$t('maintenance.alert.failedCreateMo'))
        this.$bvModal.hide('mc-modal-create-mo')
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.container-alert {
  margin-top: -25px;
  margin-bottom: 20px;
}
.custom-alert {
  border: 0 !important;
  border-radius: 0;
}
.custom-alert.mc-alert.mc-alert__info.d-flex {
  gap: 0 !important;
  padding-left: 16px !important;
}
.custom-alert__text {
  width: 100%;
  color: #344264;
  font-size: 13px;
}
.custom-alert__suffix {
  display: flex;
  color: #344264;
  justify-content: space-between;
  width: 12%;
  font-size: 13px;
}
:deep(.mc-button-action) {
  background-color: white;
  width: 100%;
  border: 1px solid $mep-primary;
  color: $mep-primary;
  font-weight: 600;
  padding: 5px 20px;
  min-width: 200px;
  white-space: nowrap;

  span {
    margin-right: 2px;
  }

  &.disabled {
    color: $teal-400;
    border: 1px solid $teal-400;
  }

  .mc-fa {
    &:before {
      color: $mep-primary;
    }
  }

  .status {
    position: relative;
    display: block;
    padding-left: 1rem;
    font-weight: 500;
    &::after {
      position: absolute;
      top: 7px;
      left: 0;
      width: 8px;
      height: 8px;
      border-radius: 100%;
      background-color: $primary;
      content: '';
    }
  }

  &:not(:last-child) {
    margin-bottom: 8px;
  }

  &:hover {
    background-color: $mep-primary;
    border-color: $mep-primary;
    border: 1px solid white;
    color: white;
    .status {
      &::after {
        background-color: white;
      }
    }
    .mc-fa {
      &:before {
        color: white;
      }
    }
  }
}
</style>
